import React, { useEffect, useMemo, useState } from "react";
import ReactModal from "react-modal";
import axios from "axios";
import Cookies from "universal-cookie";
import Swal from "sweetalert2";
import {
  FaEdit,
  FaSave,
  FaTimes,
  FaMapMarkerAlt,
  FaUser,
  FaPhone,
  FaIdCard,
  FaCalendarAlt,
  FaBuilding,
} from "react-icons/fa";

const API = "https://suadesarrollo.azurewebsites.net/api";

const AUTH = {
  username: "BasicAuth@icontec.org",
  password: "yQLzqmR7eZSuAmQZUqhY",
};

function EditarSede({
  idSede,
  nombre = "",
  direccion = "",
  nombreContacto = "",
  numeroContacto = "",
  codigoPrestadorSede = "",
  fechaAcreditacion = "",
  fechaRenovacion = "",
  especialidad = "",
  onUpdated,
}) {
  const cookies = useMemo(() => new Cookies(), []);

  const tipoUsuario = Number(cookies.get("tipo_Usuario"));

  /**
   * Solo los usuarios autorizados
   * deberían poder editar.
   */
  const puedeEditar = tipoUsuario === 1 || tipoUsuario === 2;

  const [modalIsOpen, setModalIsOpen] = useState(false);

  const [loading, setLoading] = useState(false);

  /**
   * Estado centralizado del formulario.
   * Evitamos tener 7 useState diferentes.
   */
  const [formData, setFormData] = useState({
    nombre: "",
    direccion: "",
    nombreContacto: "",
    numeroContacto: "",
    codigoPrestadorSede: "",
    fechaAcreditacion: "",
    fechaRenovacion: "",
    especialidad: "",
  });

  /**
   * Errores de validación.
   */
  const [errors, setErrors] = useState({});

  /**
   * ======================================================
   * ABRIR MODAL
   * ======================================================
   */
  const abrirModal = () => {
    setFormData({
      nombre: nombre || "",
      direccion: direccion || "",
      nombreContacto: nombreContacto || "",
      numeroContacto: numeroContacto || "",
      codigoPrestadorSede: codigoPrestadorSede || "",
      fechaAcreditacion: convertirFechaInput(fechaAcreditacion),
      fechaRenovacion: convertirFechaInput(fechaRenovacion),
      especialidad: especialidad || "",
    });

    setErrors({});
    setModalIsOpen(true);
  };

  /**
   * ======================================================
   * CERRAR MODAL
   * ======================================================
   */
  const cerrarModal = () => {
    if (loading) return;

    setModalIsOpen(false);
    setErrors({});
  };

  /**
   * ======================================================
   * CONVERTIR FECHA
   *
   * Permite trabajar con:
   *
   * 2022 02 01
   * 01/02/2022
   * 2022-02-01
   * ======================================================
   */
  function convertirFechaInput(fecha) {
    if (!fecha) return "";

    const valor = String(fecha).trim();

    /**
     * Ya viene correctamente.
     */
    if (/^\d{4}-\d{2}-\d{2}$/.test(valor)) {
      return valor;
    }

    /**
     * Formato:
     * 2022 02 01
     */
    if (/^\d{4}\s\d{2}\s\d{2}$/.test(valor)) {
      return valor.replaceAll(" ", "-");
    }

    /**
     * Formato:
     * 01/02/2022
     */
    if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(valor)) {
      const [dia, mes, anio] = valor.split("/");

      return `${anio}-${mes.padStart(2, "0")}-${dia.padStart(2, "0")}`;
    }

    return "";
  }

  /**
   * ======================================================
   * CAMBIO DE CAMPO
   * ======================================================
   */
  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    /**
     * Quitar error al escribir.
     */
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  /**
   * ======================================================
   * VALIDACIÓN
   * ======================================================
   */
  const validarFormulario = () => {
    const nuevosErrores = {};

    if (!formData.nombre.trim()) {
      nuevosErrores.nombre = "El nombre de la sede es obligatorio.";
    }

    if (!formData.direccion.trim()) {
      nuevosErrores.direccion = "La dirección es obligatoria.";
    }

    if (!formData.nombreContacto.trim()) {
      nuevosErrores.nombreContacto = "La persona de contacto es obligatoria.";
    }

    if (!formData.numeroContacto.trim()) {
      nuevosErrores.numeroContacto = "El número de contacto es obligatorio.";
    }

    setErrors(nuevosErrores);

    return Object.keys(nuevosErrores).length === 0;
  };

  /**
   * ======================================================
   * GUARDAR
   *
   * Un único PUT con todos los campos.
   *
   * PUT:
   * /api/ActualizaSede?Id={id}
   * ======================================================
   */
  const guardar = async (e) => {
    e.preventDefault();

    if (!validarFormulario()) {
      return;
    }

    setLoading(true);

    try {
      const data = {
        nombre: formData.nombre.trim(),

        direccion: formData.direccion.trim(),

        nombreContacto: formData.nombreContacto.trim(),

        numeroContacto: formData.numeroContacto.trim(),

        codigoPrestadorSede: formData.codigoPrestadorSede.trim(),

        fechaAcreditacion: formData.fechaAcreditacion || null,

        fechaRenovacion: formData.fechaRenovacion || null,

        especialidad: formData.especialidad || "",
      };

      const response = await axios.put(
        `${API}/ActualizaSede?Id=${idSede}`,
        data,
        {
          auth: AUTH,
        },
      );

      console.log("Sede actualizada:", response.data);

      /**
       * Cerrar modal.
       */
      setModalIsOpen(false);

      /**
       * Avisar al componente padre.
       */
      if (onUpdated) {
        onUpdated({
          id: idSede,
          ...data,
        });
      }

      await Swal.fire({
        icon: "success",
        title: "Sede actualizada",
        text: "La información fue actualizada correctamente.",
        timer: 1800,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error("Error actualizando sede:", error);

      Swal.fire({
        icon: "error",
        title: "No se pudo actualizar",
        text:
          error?.response?.data?.message ||
          "Ocurrió un error al actualizar la información de la sede.",
      });
    } finally {
      setLoading(false);
    }
  };

  /**
   * ======================================================
   * SI NO TIENE PERMISO
   * ======================================================
   */
  if (!puedeEditar) {
    return null;
  }

  return (
    <>
      {/* ==================================================
          BOTÓN EDITAR
      ================================================== */}

      <button
        type="button"
        className="btn btn-primary btn-sm"
        onClick={abrirModal}
        title="Editar sede"
      >
        <FaEdit className="mr-1" />
        Editar
      </button>

      {/* ==================================================
          MODAL
      ================================================== */}

      <ReactModal
        isOpen={modalIsOpen}
        onRequestClose={cerrarModal}
        shouldCloseOnOverlayClick={!loading}
        shouldCloseOnEsc={!loading}
        ariaHideApp={false}
        contentLabel="Editar sede"
        style={{
          overlay: {
            backgroundColor: "rgba(0, 0, 0, 0.55)",
            zIndex: 1050,
          },

          content: {
            top: "50%",
            left: "50%",
            right: "auto",
            bottom: "auto",
            transform: "translate(-50%, -50%)",
            width: "90%",
            maxWidth: "900px",
            maxHeight: "90vh",
            padding: 0,
            border: "none",
            borderRadius: "12px",
            overflow: "hidden",
          },
        }}
      >
        {/* ==================================================
            HEADER
        ================================================== */}

        <div
          className="d-flex justify-content-between align-items-center px-4 py-3"
          style={{
            background: "linear-gradient(135deg, #0085CA, #006fa6)",
            color: "#fff",
          }}
        >
          <div>
            <h5 className="mb-1 font-weight-bold">Editar sede</h5>

            <small>Actualice la información registrada</small>
          </div>

          <button
            type="button"
            className="btn btn-link text-white"
            onClick={cerrarModal}
            disabled={loading}
            style={{
              fontSize: "24px",
              textDecoration: "none",
            }}
          >
            <FaTimes />
          </button>
        </div>

        {/* ==================================================
            BODY
        ================================================== */}

        <form onSubmit={guardar} noValidate>
          <div
            className="p-4"
            style={{
              maxHeight: "70vh",
              overflowY: "auto",
            }}
          >
            {/* ==================================================
                INFORMACIÓN GENERAL
            ================================================== */}

            <div className="mb-4">
              <div className="d-flex align-items-center mb-3">
                <FaBuilding className="text-primary mr-2" />

                <h6 className="font-weight-bold mb-0">Información general</h6>
              </div>

              <div className="row">
                {/* NOMBRE */}

                <div className="col-md-12 mb-3">
                  <label className="font-weight-bold">
                    Nombre de la sede
                    <span className="text-danger"> *</span>
                  </label>

                  <input
                    type="text"
                    name="nombre"
                    className={`form-control ${
                      errors.nombre ? "is-invalid" : ""
                    }`}
                    value={formData.nombre}
                    onChange={handleChange}
                    placeholder="Nombre de la sede"
                    disabled={loading}
                  />

                  {errors.nombre && (
                    <div className="invalid-feedback">{errors.nombre}</div>
                  )}
                </div>

                {/* DIRECCIÓN */}

                <div className="col-md-12 mb-3">
                  <label className="font-weight-bold">
                    <FaMapMarkerAlt className="mr-1 text-danger" />
                    Dirección
                    <span className="text-danger"> *</span>
                  </label>

                  <input
                    type="text"
                    name="direccion"
                    className={`form-control ${
                      errors.direccion ? "is-invalid" : ""
                    }`}
                    value={formData.direccion}
                    onChange={handleChange}
                    placeholder="Dirección de la sede"
                    disabled={loading}
                  />

                  {errors.direccion && (
                    <div className="invalid-feedback">{errors.direccion}</div>
                  )}
                </div>
              </div>
            </div>

            <hr />

            {/* ==================================================
                CONTACTO
            ================================================== */}

            <div className="mb-4">
              <div className="d-flex align-items-center mb-3">
                <FaUser className="text-primary mr-2" />

                <h6 className="font-weight-bold mb-0">
                  Información de contacto
                </h6>
              </div>

              <div className="row">
                {/* CONTACTO */}

                <div className="col-md-6 mb-3">
                  <label className="font-weight-bold">
                    Persona de contacto
                    <span className="text-danger"> *</span>
                  </label>

                  <input
                    type="text"
                    name="nombreContacto"
                    className={`form-control ${
                      errors.nombreContacto ? "is-invalid" : ""
                    }`}
                    value={formData.nombreContacto}
                    onChange={handleChange}
                    placeholder="Nombre del contacto"
                    disabled={loading}
                  />

                  {errors.nombreContacto && (
                    <div className="invalid-feedback">
                      {errors.nombreContacto}
                    </div>
                  )}
                </div>

                {/* TELÉFONO */}

                <div className="col-md-6 mb-3">
                  <label className="font-weight-bold">
                    <FaPhone className="mr-1 text-success" />
                    Número de contacto
                    <span className="text-danger"> *</span>
                  </label>

                  <input
                    type="text"
                    name="numeroContacto"
                    className={`form-control ${
                      errors.numeroContacto ? "is-invalid" : ""
                    }`}
                    value={formData.numeroContacto}
                    onChange={handleChange}
                    placeholder="Número de contacto"
                    disabled={loading}
                  />

                  {errors.numeroContacto && (
                    <div className="invalid-feedback">
                      {errors.numeroContacto}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <hr />

            {/* ==================================================
                INFORMACIÓN DE ACREDITACIÓN
            ================================================== */}

            <div>
              <div className="d-flex align-items-center mb-3">
                <FaCalendarAlt className="text-primary mr-2" />

                <h6 className="font-weight-bold mb-0">
                  Información de acreditación
                </h6>
              </div>

              <div className="row">
                {/* CÓDIGO */}

                <div className="col-md-6 mb-3">
                  <label className="font-weight-bold">
                    <FaIdCard className="mr-1 text-primary" />
                    Código del prestador
                  </label>

                  <input
                    type="text"
                    name="codigoPrestadorSede"
                    className="form-control"
                    value={formData.codigoPrestadorSede}
                    onChange={handleChange}
                    placeholder="Código prestador"
                    disabled={loading}
                  />
                </div>

                {/* ESPECIALIDAD */}

                <div className="col-md-6 mb-3">
                  <label className="font-weight-bold">Acreditada</label>

                  <select
                    name="especialidad"
                    className="form-control"
                    value={formData.especialidad}
                    onChange={handleChange}
                    disabled={loading}
                  >
                    <option value="">Seleccione</option>

                    <option value="Si">Sí</option>

                    <option value="No">No</option>
                  </select>
                </div>

                {/* FECHA ACREDITACIÓN */}

                <div className="col-md-6 mb-3">
                  <label className="font-weight-bold">
                    Fecha de acreditación
                  </label>

                  <input
                    type="date"
                    name="fechaAcreditacion"
                    className="form-control"
                    value={formData.fechaAcreditacion}
                    onChange={handleChange}
                    disabled={loading || tipoUsuario !== 1}
                  />

                  {tipoUsuario !== 1 && (
                    <small className="text-muted">
                      Solo el administrador puede modificar esta fecha.
                    </small>
                  )}
                </div>

                {/* FECHA RENOVACIÓN */}

                <div className="col-md-6 mb-3">
                  <label className="font-weight-bold">
                    Fecha de renovación
                  </label>

                  <input
                    type="date"
                    name="fechaRenovacion"
                    className="form-control"
                    value={formData.fechaRenovacion}
                    onChange={handleChange}
                    disabled={loading || tipoUsuario !== 1}
                  />

                  {tipoUsuario !== 1 && (
                    <small className="text-muted">
                      Solo el administrador puede modificar esta fecha.
                    </small>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div
            className="d-flex justify-content-end border-top px-4 py-3"
            style={{
              background: "#f8f9fa",
            }}
          >
            <button
              type="button"
              className="btn btn-secondary mr-2"
              onClick={cerrarModal}
              disabled={loading}
            >
              <FaTimes className="mr-1" />
              Cancelar
            </button>

            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm mr-2"
                    role="status"
                    aria-hidden="true"
                  />
                  Guardando...
                </>
              ) : (
                <>
                  <FaSave className="mr-2" />
                  Guardar cambios
                </>
              )}
            </button>
          </div>
        </form>
      </ReactModal>
    </>
  );
}

export default EditarSede;
